// TerraDraw service for modern drawing functionality
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode, ValidateNotSelfIntersecting, type GeoJSONStoreFeatures } from 'terra-draw'
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter'
import type * as maplibregl from 'maplibre-gl'

export class TerraDrawService {
  private static instance: TerraDrawService | null = null;

  private draw: TerraDraw | null = null;
  private adapter: TerraDrawMapLibreGLAdapter<maplibregl.Map> | null = null;
  private map: maplibregl.Map | null = null;
  private isInitialized: boolean = false;

  static getInstance(): TerraDrawService {
    if (!TerraDrawService.instance) {
      TerraDrawService.instance = new TerraDrawService();
    }
    return TerraDrawService.instance;
  }

  async initialize(map: maplibregl.Map): Promise<void> {
    if (this.isInitialized && this.map === map) return;

    try {
      // Validate map instance
      if (!map) {
        throw new Error('Map instance is required for TerraDraw initialization');
      }

      // Store map reference
      this.map = map;

      this.adapter = new TerraDrawMapLibreGLAdapter({
        map: map
      });

      this.draw = new TerraDraw({
        adapter: this.adapter,
        modes: [
          new TerraDrawPolygonMode({
            editable: true,
            // Reject bowtie polygons at draw time. Without this the backend
            // stores a self-intersecting boundary that RGeo then refuses to
            // compute a centroid or area for.
            validation: ValidateNotSelfIntersecting,
          }),
          new TerraDrawSelectMode({
            // Also needed here: dragging a vertex or midpoint can pull an
            // already-valid polygon across one of its own edges.
            validation: ValidateNotSelfIntersecting,
            flags: {
              polygon: {
                feature: {
                  draggable: true,
                  coordinates: {
                    midpoints: true,
                    draggable: true,
                    deletable: true,
                  },
                },
              },
            },
          }),
        ]
      });

      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize TerraDraw:', error);
      this.isInitialized = false;
      this.map = null;
      this.adapter = null;
      this.draw = null;
      throw error;
    }
  }

  async start(): Promise<void> {
    if (!this.draw) {
      throw new Error('TerraDraw not initialized. Call initialize() first.');
    }

    if (!this.map) {
      throw new Error('Map instance not available');
    }

    try {
      await this.draw.start();
      this.startPolygonMode();
    } catch (error) {
      console.error('Failed to start TerraDraw:', error);
      throw error;
    }
  }

  async stop(): Promise<void> {
    if (!this.draw) return;

    try {
      await this.draw.stop();
    } catch (error) {
      console.error('Failed to stop TerraDraw:', error);
    }
  }

  on(event: 'finish', callback: (id: string | number, context: any) => void): void {
    if (!this.draw) return;

    this.draw.on(event, callback);
  }

  off(event: 'finish', callback: (id: string | number, context: any) => void): void {
    if (!this.draw) return;

    this.draw.off(event, callback);
  }

  setMode(mode: string): void {
    if (!this.draw) return;

    this.draw.setMode(mode);
  }

  startPolygonMode(): void {
    this.setMode('polygon');
  }

  startSelectMode(): void {
    this.setMode('select');
  }

  selectFeature(id: string | number): void {
    if (!this.draw) return;

    this.draw.selectFeature(id);
  }

  getFeatures(): GeoJSONStoreFeatures[] {
    if (!this.draw) return [];

    return this.draw.getSnapshot();
  }

  getFirstPolygonFeature(): GeoJSONStoreFeatures | null {
    return this.getFeatures().find(feature => feature.geometry.type === 'Polygon') ?? null;
  }

  clearFeatures(): void {
    if (!this.draw) return;

    this.draw.clear();
  }

  loadFeatures(features: GeoJSONStoreFeatures[]): void {
    if (!this.draw) return;

    // TerraDraw requires properties.mode to match the originating draw mode name
    // so select-mode flags (keyed under "polygon") apply to loaded features.
    const prepared = features.map((feature) => ({
      ...feature,
      properties: {
        ...(feature.properties ?? {}),
        mode: 'polygon',
      },
    })) as GeoJSONStoreFeatures[];

    this.draw.addFeatures(prepared);
  }
}

// Export a singleton instance
export const terraDrawService = TerraDrawService.getInstance();
